/**
 * Declarative description of the fab floor.
 *
 * The cleanroom is laid out bay-and-chase, the way a real fab is: a central
 * personnel aisle, process tools in bays either side, and the noisy support
 * equipment pushed to the ends. Keeping the layout as data rather than as
 * scene-building code means the panel registry, the minimap, and the assistant
 * all read the same source, and adding a tool is a data edit.
 *
 * THE FLOOR IS THE PROCESS FLOW
 *
 * The tools are ordered along the aisle in the order a wafer meets them:
 * polysilicon at one end, a tested part at the other. Walking the length of the
 * building is walking the process, and that is deliberate - a technician who
 * knows only their own bay is the problem this curriculum exists to fix. The
 * `zone` field names which stage of the flow a tool belongs to.
 *
 * ONE HALL, AND WHY THAT IS A COMPROMISE
 *
 * A real operation does not put die attach in the same room as lithography.
 * Front-end wafer fabrication runs at ISO 3-5 with full gowning; assembly and
 * test run at a far lower class in a separate building, often on another
 * continent. Putting them in one hall here trades that truth for the ability to
 * walk the whole flow without a loading screen. It is a simulation compromise,
 * it is flagged in the assembly zone's own signage, and splitting the halls is
 * a refinement rather than a rewrite - nothing but this file's positions would
 * have to change.
 *
 * THE SAME IS TRUE AT THE OTHER END, and more so. CZ-07 and WPR-08 are a
 * crystal puller and a wafer-prep line, and those do not live in a wafer fab
 * AT ALL: an ingot is grown, sliced, lapped, etched and polished at a
 * substrate supplier, and what arrives at a fab's dock is a finished polished
 * wafer in a cassette. A puller is two storeys of vessel in its own high bay
 * with a graphite hot zone in it, not a 2.4 m column on a cleanroom aisle.
 * They are here because the curriculum starts at sand, and a student who has
 * never seen where the wafer came from treats it as a given. Same trade, same
 * flag, same fix if it is ever worth making: three positions in this file.
 *
 * FAC-06 and GAS-19 are the third. Gas cabinets and the bulk valve manifolds
 * are in a gas room or on the sub-fab level in any real fab, behind a wall
 * with its own monitoring and its own exhaust - not on the production aisle
 * where a technician walks past them all shift. They stand here because gas
 * handling is a competency this curriculum teaches at a face, and a hazard a
 * student cannot walk up to is a hazard they do not learn.
 */

import type { Vector3Tuple } from 'three'
import { HALL_LAYOUT, onHallChange, planIdOf, type HallLayout } from './hall'
import { getPlan, type FloorPlan } from './floorPlan'

import { t } from '../i18n/i18n'

export type ToolStatus = 'live' | 'pending'

/**
 * Stage of the process flow. Ordered, and the order is the order of the aisle.
 */
export type FlowZone =
  | 'entry'
  | 'substrate'
  | 'frontend'
  | 'metrology'
  | 'interconnect'
  | 'sort'
  | 'assembly'
  | 'quantum'
  | 'support'

/** Tool silhouette. Purely visual; the panel is chosen by `id`. */
export type ToolKind =
  | 'gowning'
  | 'crystal'
  | 'waferprep'
  | 'epi'
  | 'wetbench'
  | 'implanter'
  | 'etcher'
  | 'deposition'
  | 'litho'
  | 'patterning'
  | 'furnace'
  | 'metrology'
  | 'facilities'
  | 'cmp'
  | 'plating'
  | 'inspection'
  | 'probe'
  | 'dicing'
  | 'bonding'
  | 'packaging'
  | 'test'
  | 'gas'
  | 'euv'
  | 'ebeam'
  | 'cryostat'

/**
 * A place on the floor, and the hardware standing in it.
 *
 * Deliberately data and nothing else: where it stands, what silhouette to
 * build, how many load ports to draw. It carries no physics and no interface.
 * What you OPERATE at a tool is its tool panel, which lives in tools/ and is
 * looked up by this `id` - see tools/types.ts for why the two are kept apart.
 */
export interface Tool {
  /** Stable id, also the tool panel's registry key. */
  id: string
  /**
   * Name stencilled on the tool, as it would appear on a real asset tag. Not
   * localised: it is a physical label on physical hardware.
   *
   * Numbered by install order rather than by process order, which is how real
   * asset tags work and which is why ETCH-01 sits in the middle of the flow.
   */
  assetTag: string
  status: ToolStatus
  /** Which stage of the process flow this tool belongs to. */
  zone: FlowZone
  /** Floor position, metres. */
  position: Vector3Tuple
  /** Tool silhouette to build. */
  kind: ToolKind
  /** Accent colour for the status tower and HMI trim. */
  accent: number
  /** Number of load ports on the front of the mainframe. */
  loadPorts: number
  /** Process chambers clustered on the transfer module. */
  chambers: number
  /**
   * Mainframe footprint in metres. Optional; the default is a mid-sized
   * cluster tool. A crystal puller is a tall narrow column and a wet bench is a
   * long low tank, and letting the data say so is cheaper than a silhouette
   * function per tool kind.
   */
  size?: { w: number; h: number; d: number }
}

/**
 * Cleanroom envelope, metres.
 *
 * THE HALL IS AS WIDE AS THE WORK IN IT. The aisle was 3.2 m of half-width
 * with the bulkheads 5.6 m out, which put 10.1 m between the two rows of
 * tool faces. Nothing in a fab is 10 m across. A real service aisle is 4-5
 * m - narrow enough that the tool you are working on and the one behind you
 * are both within reach, and that the hoist track over the load ports is
 * over YOUR head too. At 10 m the two rows were scenery on either side of a
 * concourse, the transport system was a detail at the ceiling joint, and the
 * hall photographed like an airport pier.
 *
 * The cross-section this width has to hold: 0.85 m to stand at a tool face,
 * both sides; a robot lane inside each walkway line; and a walkway between
 * them wide enough for two people to pass. That is 5.4 m face to face,
 * which is what these numbers give.
 *
 * `bayDepth` moves the other way by exactly as much, so `bayDepth +
 * aisleHalfWidth` - the room's half width, and the basis of the floor and
 * ceiling tiling, the chase, the sub-fab stairwell and the airlock - does
 * not change. Only the wall between the aisle and the chase moves inward.
 */
export const FAB = {
  aisleHalfWidth: 1.85,
  bayDepth: 10.35 as number,
  // The bay-and-chase hall's length. A ballroom packs the row tighter and
  // the hall follows the row: `packRow` below rewrites this once, at
  // import, before anything else reads it - and `bayDepth` with it, for
  // the ballroom's second row and outer aisle.
  length: 162 as number,
  ceilingHeight: 3.6,
  /** Height of the raised access floor above the subfab slab. */
  floorHeight: 0.0,
} as const

/** Default mainframe footprint when a tool does not state its own. */
export const DEFAULT_TOOL_SIZE = { w: 3.4, h: 2.1, d: 2.6 }

export const toolSize = (s: Tool) => s.size ?? DEFAULT_TOOL_SIZE

/**
 * THE BULKHEAD LINE. This hall is bay-and-chase in the literal sense: the
 * tools are not islands a technician walks around but faces set into a
 * wall, with the mainframe, its chambers and its plumbing in the chase
 * behind. That is the 200 mm-era layout, and it persists in older, analog,
 * power and MEMS fabs; a new 300 mm fab is mostly a ballroom - one open
 * floor with the mainframes in view and the utilities in the sub-fab -
 * because the FOUP and the EFEM moved the Class 1 volume inside the tool.
 * It is chosen here on purpose: the chase behind a bulkhead is a room a
 * student can walk into and follow a gas line from tool to header to wall,
 * and a ballroom has no such room to show. The ballroom is offered as well
 * (hall.ts, `BALLROOM`): the same rows on the same line with the wall left
 * out, so these numbers hold under both.
 *
 * What the aisle shows of a tool is its front - load ports, HMI,
 * placard, light tower - standing a little proud of a continuous white
 * wall that runs the length of the hall. This is that wall's distance from
 * the aisle centre, and how far a face stands out of it. Every tool in a
 * bay is built to this line whatever its own depth; the tools on the
 * aisle's axis (gowning, bulk gas) are free-standing and keep theirs.
 */
export const BULKHEAD_Z = 3.25
export const FACE_PROUD = 0.55

/**
 * How far a bay tool's own position sits behind its face.
 *
 * Every tool in a bay is built to the bulkhead whatever its `position`, so
 * the position is not where its face is - it is where its mainframe stands
 * in the chase. A handful of things in the scene are nonetheless placed
 * from it: the approach point, the HMI viewpoint, the cutaway pedestal's
 * collider. They all assume a FIXED offset between a tool's position and
 * its face, so when the bulkhead moves the row has to move with it.
 *
 * This is that offset, and `|position[2]|` must equal `BULKHEAD_Z + BODY_SET`
 * for every tool in a bay. Change the bulkhead, change the row.
 */
export const BODY_SET = 0.6

/** World |z| of a tool's front face. */
export const toolFrontZ = (s: Tool): number =>
  s.position[2] === 0 ? toolSize(s).d / 2 : BULKHEAD_Z - FACE_PROUD

/**
 * World z of a tool's front face, signed: on the tool's own side of the
 * aisle, or for a tool on the axis, the +z side its front turns to.
 */
export const toolFaceZ = (s: Tool): number =>
  s.position[2] === 0 ? toolFrontZ(s) : Math.sign(s.position[2]) * toolFrontZ(s)

/**
 * Every tool from polysilicon to a tested part, in flow order.
 *
 * A tool that is not yet simulated is still placed and enterable, and
 * carries its scope: what arrives, what leaves, the macroscopic protocol, the
 * microscopic science under it, the solver it will be built on, and the
 * technician competency it exists to serve. That is deliberately the same
 * information a panel needs before anyone writes its physics, so the scope is a
 * work plan rather than a placeholder.
 */
export const TOOLS: Tool[] = [
  // --- entry ---------------------------------------------------------------
  {
    id: 'gowning',
    assetTag: 'GOWN-00',
    status: 'live',
    zone: 'entry',
    position: [-71, 0, 0],
    kind: 'gowning',
    accent: 0xdfe3e8,
    loadPorts: 0,
    chambers: 0,
    size: { w: 5.0, h: 2.6, d: 2.4 },
  },

  // --- substrate: silicon into wafers --------------------------------------
  {
    id: 'crystal',
    assetTag: 'CZ-07',
    status: 'live',
    zone: 'substrate',
    position: [-61, 0, -3.85],
    kind: 'crystal',
    // A puller is a tall column with the furnace at the bottom - the one tool
    // on the floor that is taller than the person operating it by design.
    // (A real one is two storeys and lives in its own high bay; this is as
    // tall as a 3.6 m ceiling lets it be with the pull head still under it.)
    accent: 0xffb454,
    loadPorts: 0,
    chambers: 1,
    size: { w: 2.2, h: 2.4, d: 2.2 },
  },
  {
    id: 'waferprep',
    assetTag: 'WPR-08',
    status: 'live',
    zone: 'substrate',
    position: [-61, 0, 3.85],
    kind: 'waferprep',
    accent: 0xd9c58a,
    loadPorts: 2,
    chambers: 0,
    size: { w: 4.6, h: 1.8, d: 2.6 },
  },

  {
    id: 'epi',
    assetTag: 'EPI-20',
    status: 'live',
    zone: 'substrate',
    position: [-54, 0, -3.85],
    kind: 'epi',
    // Warm coral: it lives with the substrate tools but glows like a furnace.
    accent: 0xff8f6b,
    loadPorts: 2,
    chambers: 1,
    // A single-wafer lamp reactor: squat chamber, quartz dome over it.
    size: { w: 3.6, h: 2.0, d: 3.0 },
  },

  // --- front end: building the device --------------------------------------
  {
    id: 'clean',
    assetTag: 'CLN-09',
    status: 'live',
    zone: 'frontend',
    position: [-46, 0, -3.85],
    kind: 'wetbench',
    accent: 0x6fd8e8,
    loadPorts: 2,
    chambers: 0,
    // A wet bench is long and low: a row of baths, not a mainframe.
    size: { w: 5.4, h: 1.5, d: 2.4 },
  },
  {
    id: 'implant',
    assetTag: 'IMP-10',
    status: 'live',
    zone: 'frontend',
    position: [-46, 0, 3.85],
    kind: 'implanter',
    accent: 0xb388ff,
    loadPorts: 2,
    chambers: 1,
    // The beamline is the tool. Implanters are the largest thing in the bay.
    size: { w: 6.2, h: 2.3, d: 2.8 },
  },
  {
    id: 'etch',
    assetTag: 'ETCH-01',
    status: 'live',
    zone: 'frontend',
    position: [-32, 0, -3.85],
    kind: 'etcher',
    accent: 0x4ac6ff,
    loadPorts: 3,
    chambers: 4,
  },
  {
    id: 'deposition',
    assetTag: 'DEP-02',
    status: 'live',
    zone: 'frontend',
    position: [-32, 0, 3.85],
    kind: 'deposition',
    accent: 0x7ee0a8,
    loadPorts: 2,
    chambers: 3,
  },
  {
    id: 'litho',
    assetTag: 'LITH-03',
    status: 'live',
    zone: 'frontend',
    position: [-19, 0, -3.85],
    kind: 'litho',
    accent: 0xffc857,
    loadPorts: 4,
    chambers: 2,
  },
  {
    // A patterning cell is not a machine, it is a route - eight steps that
    // send the wafer back through LITH-03's neighbours twice. It sits next to
    // the scanner because that is where the mandrel is printed and because
    // that is where a technician goes to ask why the pitch walked.
    id: 'patterning',
    assetTag: 'PAT-21',
    status: 'live',
    zone: 'frontend',
    position: [-19, 0, 3.85],
    kind: 'patterning',
    accent: 0x9be564,
    loadPorts: 2,
    chambers: 0,
  },
  {
    id: 'thermal',
    assetTag: 'THRM-04',
    status: 'live',
    zone: 'frontend',
    // Across from EPI-20 - the one bay that was standing empty. THRM-04
    // used to share PAT-21's spot, two machines built on the same
    // spot: the picker flipped coins between them and both placards hung in
    // one bay. Front-end thermal beside epi and clean is also where a real
    // flow wants the furnace.
    position: [-54, 0, 3.85],
    kind: 'furnace',
    accent: 0xff8a5c,
    loadPorts: 2,
    chambers: 1,
    size: { w: 3.4, h: 2.6, d: 2.6 },
  },

  // --- the leading edge: EUV and the electron beam that measures it ----------
  //
  // A bay of its own beside LITH-03, because that is where a fab puts them:
  // the EUV scanner shares LITH-03's coat/develop flow, and the CD-SEM is
  // the first tool every exposed wafer visits - after-develop inspection -
  // before anybody lets it near an etcher.
  {
    id: 'euv',
    assetTag: 'EUV-22',
    status: 'live',
    zone: 'frontend',
    position: [-6, 0, -3.85],
    kind: 'euv',
    // Violet: the light it makes is past the end of the violet.
    accent: 0xb48cff,
    loadPorts: 2,
    chambers: 1,
    // THE FACE IS THE TRACK, NOT THE SCANNER. An EUV scanner takes no
    // carriers: wafers reach it coated, through the coat/develop track
    // docked in line with it, and the track's front end is where the
    // hoists put the lots. So this body is the track - low, under the
    // transport like every face - and the scanner is the enclosure standing
    // beside it (SILHOUETTES.euv), the largest thing on the floor, with its
    // tin source at the back of it, in the chase, fed through the floor by
    // the beam tube from the drive laser in the sub-fab.
    size: { w: 3.0, h: 2.05, d: 3.2 },
  },
  {
    id: 'ebeam',
    assetTag: 'SEM-23',
    status: 'live',
    zone: 'metrology',
    position: [-6, 0, 3.85],
    kind: 'ebeam',
    accent: 0x5fd6c8,
    loadPorts: 2,
    chambers: 1,
    // A closed cabinet on vibration isolation, the column inside it: only
    // its cover shows, on the roof, set back behind the transport's path.
    size: { w: 3.6, h: 2.05, d: 2.8 },
  },

  // --- metrology and utilities ---------------------------------------------
  {
    id: 'metrology',
    assetTag: 'MET-05',
    status: 'live',
    zone: 'metrology',
    position: [7, 0, -3.85],
    kind: 'metrology',
    accent: 0xc9a0ff,
    loadPorts: 2,
    chambers: 2,
  },
  {
    id: 'facilities',
    assetTag: 'FAC-06',
    status: 'live',
    zone: 'metrology',
    position: [7, 0, 3.85],
    kind: 'facilities',
    accent: 0xff6b8a,
    loadPorts: 0,
    chambers: 0,
    size: { w: 4.2, h: 2.1, d: 2.6 },
  },

  // --- planarisation and interconnect --------------------------------------
  {
    id: 'cmp',
    assetTag: 'CMP-11',
    status: 'live',
    zone: 'interconnect',
    position: [20, 0, -3.85],
    kind: 'cmp',
    accent: 0x8fd694,
    loadPorts: 2,
    chambers: 3,
    size: { w: 4.0, h: 1.9, d: 3.0 },
  },
  {
    id: 'plating',
    assetTag: 'ECD-12',
    status: 'live',
    zone: 'interconnect',
    position: [20, 0, 3.85],
    kind: 'plating',
    accent: 0xffa0c0,
    loadPorts: 2,
    chambers: 4,
    size: { w: 4.2, h: 2.0, d: 2.8 },
  },

  // --- inspection and sort -------------------------------------------------
  {
    id: 'inspection',
    assetTag: 'INSP-13',
    status: 'live',
    zone: 'sort',
    position: [33, 0, -3.85],
    kind: 'inspection',
    accent: 0x9fe0ff,
    loadPorts: 2,
    chambers: 1,
  },
  {
    id: 'probe',
    assetTag: 'PRB-14',
    status: 'live',
    zone: 'sort',
    position: [33, 0, 3.85],
    kind: 'probe',
    accent: 0xffe066,
    loadPorts: 2,
    chambers: 1,
    // LOW, BECAUSE THE HEAD GOES ON TOP. A prober's own body is about
    // 1.6 m and the test head docked over its chuck brings it to two
    // metres, which is also the only way the head fits: the monorail's
    // vehicles sweep from 2.09 m up and out to 2.855 m across the hall,
    // and the tool face stands at 2.70 - so anything above 2.09 on this
    // face is in the carriers' path. At the default 2.1 m body the head
    // sat squarely in it.
    size: { w: 3.4, h: 1.6, d: 2.6 },
  },

  // --- assembly, packaging and test ----------------------------------------
  //
  // Everything from here is a lower cleanliness class and, in a real operation,
  // a different building. See the header note.
  {
    id: 'dicing',
    assetTag: 'DICE-15',
    status: 'live',
    zone: 'assembly',
    position: [46, 0, -3.85],
    kind: 'dicing',
    accent: 0xff9a76,
    loadPorts: 2,
    chambers: 1,
    size: { w: 3.0, h: 1.8, d: 2.4 },
  },
  {
    id: 'bonding',
    assetTag: 'BOND-16',
    status: 'live',
    zone: 'assembly',
    position: [46, 0, 3.85],
    kind: 'bonding',
    accent: 0xa0ffcf,
    loadPorts: 2,
    chambers: 2,
    size: { w: 3.2, h: 1.7, d: 2.4 },
  },
  {
    id: 'packaging',
    assetTag: 'PKG-17',
    status: 'live',
    zone: 'assembly',
    position: [58, 0, -3.85],
    kind: 'packaging',
    accent: 0xd0a0ff,
    loadPorts: 2,
    chambers: 2,
    size: { w: 4.4, h: 2.2, d: 2.8 },
  },
  {
    id: 'finaltest',
    assetTag: 'FT-18',
    status: 'live',
    zone: 'assembly',
    position: [58, 0, 3.85],
    kind: 'test',
    accent: 0x66e0b0,
    loadPorts: 2,
    chambers: 0,
    // A handler with its test head docked on the roof, and the same
    // clearance as PRB-14 above: body plus head has to stay under the
    // transport at 2.09 m.
    size: { w: 3.6, h: 1.65, d: 2.2 },
  },

  // --- quantum devices: the same fab, a different product -------------------
  //
  // Everything a qubit chip needs is made on the tools already in this hall
  // - the junction's oxide is THRM-04's problem at a hundred times the
  // sensitivity, the spin qubit's well is grown on EPI-20 - and none of it
  // can be tested warm. This is where it is cooled to millikelvin and asked
  // what frequency it came out at. Past final test, because a quantum chip
  // is finished before it is measured, and in a bay of its own.
  {
    id: 'quantum',
    assetTag: 'QDT-24',
    status: 'live',
    zone: 'quantum',
    position: [71, 0, -3.85],
    kind: 'cryostat',
    // The gold of a dilution refrigerator's plated stages.
    accent: 0xf2c14e,
    // NO LOAD PORT. A millikelvin dilution refrigerator is not fed from
    // carriers: a chip is diced, wire-bonded into a sample holder at the
    // bench in front of this face, and loaded by hand, which is a day's
    // cool-down per load. The face is the control cabinet; the fridge hangs
    // in its frame beside it (SILHOUETTES.cryostat).
    loadPorts: 0,
    chambers: 1,
    size: { w: 2.8, h: 2.05, d: 2.6 },
  },

  // --- sub-fab and chemical supply -----------------------------------------
  {
    id: 'gas',
    assetTag: 'GAS-19',
    status: 'live',
    zone: 'support',
    // INSIDE THE BUILDING. When the hall was 136 m, at x = 66 a 5 m tool
    // centred there ran from 63.5 to 68.5 and the end wall had its inner
    // face at 67.9 (`FAB.length / 2` less the wall's own 0.2 m): the last
    // 0.6 m of the tool was inside the wall, and with it the whole of the
    // first of its six cylinder cabinets, which stood buried where nobody
    // would ever see that it was missing. The same 3.4 m from the end wall
    // puts the row clear with room to walk round the end of it.
    position: [77.6, 0, 0],
    kind: 'gas',
    accent: 0xff5c5c,
    loadPorts: 0,
    chambers: 0,
    size: { w: 5.0, h: 2.4, d: 2.2 },
  },
]

/**
 * Display text for a tool. Held in the catalogue rather than here so the names,
 * module titles and approach blurbs translate with everything else - this file
 * stays geometry and identity.
 */
/** The hardware's name: "Inductively Coupled Plasma Etcher". */
export const toolName = (s: Tool): string => t(`station.${s.id}.Name`)

/**
 * The unit process this tool runs: "Plasma Etch". A different string from the
 * name above and easy to mistake for it - one is the machine, the other is what
 * it does to a wafer. The approach card shows this one first, because that is
 * what a wafer comes here for.
 */
export const toolProcess = (s: Tool): string => t(`station.${s.id}.Process`)

/** One line on what this tool does, for the approach card. */
export const toolBlurb = (s: Tool): string => t(`station.${s.id}.Blurb`)

export const getTool = (id: string) => TOOLS.find((s) => s.id === id)

/**
 * The bay a tool stands in, stencilled the way the hanging sign reads it:
 * "01" nearest gowning, counting up the aisle. Tools on the aisle's own
 * axis (gowning, bulk gas) are in no bay and answer ''.
 *
 * ONE definition, because there were three. The hanging bay signs
 * (builders.ts), the andon boards (bays.ts) and the technicians' briefs
 * (talk/brief.ts) each derived this from the sorted tool positions in their
 * own copy of the same four lines. They agreed - I checked - but nothing
 * made them agree, and the first person to add a tool between two bays
 * would have had a technician saying bay 07 under a sign reading 08.
 */
const bayXs = (): number[] =>
  [...new Set(TOOLS.filter((t) => t.position[2] !== 0).map((t) => t.position[0]))].sort((a, b) => a - b)

export const bayNumber = (tool: Tool): string => {
  if (tool.position[2] === 0) return ''
  const i = bayXs().indexOf(tool.position[0])
  return i < 0 ? '' : String(i + 1).padStart(2, '0')
}

/**
 * The bay a point on the aisle is level with, and the tools standing in
 * it. For anyone whose position is not a tool's - the technician walking
 * the aisle, who is asked "which bay is this?" like everyone else and
 * whose honest answer is "level with 04, heading east".
 */
export const bayAt = (x: number): Tool[] => {
  const xs = bayXs()
  if (!xs.length) return []
  const nearest = xs.reduce((best, bx) => (Math.abs(bx - x) < Math.abs(best - x) ? bx : best), xs[0])
  return TOOLS.filter((t) => t.position[2] !== 0 && t.position[0] === nearest)
}

/**
 * Unit direction, along z, from a tool toward the central aisle.
 *
 * Derived from where the tool actually stands rather than from its rotation.
 * Deriving it from rotation is what put every load port and HMI facing the back
 * wall in the first walkthrough - the two encode the same fact, and when they
 * disagreed there was no way to tell which was wrong. Position is the fact;
 * rotation follows from it.
 */
export const aisleDirection = (tool: Tool): number =>
  tool.position[2] === 0 ? 1 : -Math.sign(tool.position[2])

/**
 * The yaw that turns a tool's front - its local -z, where the load ports and
 * the HMI live - toward the aisle.
 */
export const facingYaw = (tool: Tool): number =>
  aisleDirection(tool) > 0 ? Math.PI : 0

/**
 * How far each kind's side furniture (SILHOUETTES in builders.ts) reaches
 * past the face along the wall, on the face's local -x and +x sides, so
 * the wall furniture keeps clear of it - and so the row can be packed.
 */
export const SIDE_REACH: Partial<Record<ToolKind, { minus: number; plus: number }>> = {
  litho: { minus: 4.5, plus: 0.3 },
  etcher: { minus: 0.3, plus: 1.15 },
  epi: { minus: 0.3, plus: 1.15 },
  test: { minus: 0.8, plus: 1.9 },
  deposition: { minus: 1.0, plus: 0.3 },
  // The monitor-wafer cabinet beside MET-05.
  metrology: { minus: 1.0, plus: 0.3 },
  implanter: { minus: 0.3, plus: 1.05 },
  wetbench: { minus: 1.1, plus: 0.3 },
  cmp: { minus: 0.3, plus: 1.15 },
  plating: { minus: 0.3, plus: 1.1 },
  // The tester rack past the front end on one side, the head manipulator's
  // stand at the other shoulder.
  probe: { minus: 0.7, plus: 1.8 },
  // The scanner enclosure beside the track's face.
  euv: { minus: 4.7, plus: 0.3 },
  // The high-voltage rack on one side, the operator's console desk on the
  // other. Each allowance is the furniture plus the door frame's margin.
  ebeam: { minus: 1.3, plus: 1.75 },
  // The fridge in its frame, the gas-handling cabinet and the pulse-tube
  // compressor on one side; the microwave rack on the other.
  cryostat: { minus: 3.2, plus: 1.3 },
}

/**
 * THE ROW, PACKED FOR A BALLROOM.
 *
 * The positions above are the bay-and-chase hall's: a bay every 13 m or
 * so, because a bay is a room and a room has a door, a window and a wall
 * of fittings beside its tool. A ballroom has none of that between two
 * tools - a divider carrying the bay station, and the clear floor a cart
 * needs - and a 300 mm fab packs its row accordingly. So in a ballroom the
 * row is re-laid from what each bay actually occupies: the wider of its two
 * faces with their side furniture, then a fixed gap for the divider, the
 * bin, the extinguisher and the eyewash, then the next. The hall's length
 * follows the packed span with the same end margins the authored hall has
 * (20 m before the first bay for the airlock, gowning and the stocker,
 * 10 m after the last for the gas farm), and the two axis tools keep their
 * authored offsets from the row's ends.
 *
 * Derived, not written, for the reason `bayNumber` is: the order of the
 * bays, and so every bay number, sign, brief and work order, is exactly
 * the bay-and-chase order. Only the distances change. The check scripts
 * run under the default layout and never see this.
 */
const BALLROOM_BAY_GAP = 3.0
const END_BEFORE = 20
const END_AFTER = 10
/**
 * The shortest hall a floor plan may produce.
 *
 * The sub-fab is one fixed section of the building, x = 2..42 on the slab
 * below (subfabLayout.ts SUBFAB), and it is carried unchanged from hall to
 * hall - its skids, racks and stair are laid by hand against those
 * numbers. A hall shorter than that section would have a walkable room
 * running on underground past its own end wall. So the row is packed as
 * tight as its plan allows and the hall is then held to at least this,
 * with the spare length past the gas farm: SUBFAB.maxX plus the six metres
 * the authored hall keeps between its last bay and its end wall, doubled
 * because the hall is centred on x = 0. subfab-check asserts the pairing.
 */
export const MIN_HALL_LENGTH = 96

/**
 * THE SECOND ROW. A ballroom's service aisle runs between two rows' BACKS:
 * each row faces its own production aisle, and the technician's aisle is
 * the strip behind both. Fab B has that second row - every bay tool built
 * a second time, turned half a turn about the service aisle's centreline
 * at its own x, so its back faces the first row's back across the aisle
 * and its front faces an outer aisle of the main aisle's width. The hall
 * widens to hold it: face line 2.7 m, mainframe backs to ~5.7, service
 * aisle to 8.0, the second row's backs there and its faces at 11.0, an
 * aisle edge at 11.85, and the outer wall 3.7 m past that.
 *
 * The copies are geometry, not tools: they carry the next asset number of
 * their kind (ETCH-02 behind ETCH-01, which is exactly how a fab that owns
 * two etchers tags them), take carriers and a light tower like any face,
 * and cannot be opened - the catalogue, the physics and the work orders
 * belong to the twenty-five tools in TOOLS, and a copy claiming to be one
 * of them would be a screen with nothing behind it.
 */
export const SECOND_ROW_PIVOT_Z = 6.85
/** The outer aisle's half width - the same walkway as the main aisle's. */
export const OUTER_AISLE_HALF = 1.85
/** Fab B's half depth from the aisle edge to the outer wall, so `FAB.bayDepth` there. */
const BALLROOM_BAY_DEPTH = 13.7
/** The outer aisle's centreline, for the builders that lay it. */
export const outerAisleZ = (): number => FAB.bayDepth + FAB.aisleHalfWidth - OUTER_AISLE_HALF

/** The asset tag a second machine of the same kind carries: the next number. */
export const secondTag = (tag: string): string => {
  const m = /^(.*?)(\d+)$/.exec(tag)
  return m ? `${m[1]}${String(Number(m[2]) + 1).padStart(m[2].length, '0')}` : `${tag}-B`
}

/**
 * How far a bay reaches either side of its own centreline, along the aisle:
 * the wider of its two faces with their side furniture.
 *
 * The sides must already be assigned when this is called - `aisleDirection`
 * reads the sign of z, and which end of a face its furniture stands at
 * depends on which way the face turns.
 */
function bayExtent(faces: Array<{ tool: Tool; facing: number }>): { l: number; r: number } {
  let l = 0
  let r = 0
  for (const { tool, facing } of faces) {
    const half = toolSize(tool).w / 2
    const reach = SIDE_REACH[tool.kind] ?? { minus: 0.3, plus: 0.3 }
    // The face's local x runs against world x on the +z side.
    l = Math.max(l, half + (facing > 0 ? reach.plus : reach.minus))
    r = Math.max(r, half + (facing > 0 ? reach.minus : reach.plus))
  }
  return { l, r }
}

/**
 * Where each bay's centreline falls, and how long a hall that holds them is.
 *
 * Separated from the placing so the authoring dialog can show a floor's
 * length and bay count WITHOUT building it (`planFootprint`). One piece of
 * arithmetic, two callers, and no way for the preview to disagree with the
 * hall it previews.
 */
function packOffsets(
  bays: Array<Array<{ tool: Tool; facing: number }>>,
  gap: number,
): { xs: number[]; span: number; length: number; x0: number } {
  const extents = bays.map(bayExtent)
  const xs = [0]
  for (let i = 1; i < bays.length; i++) xs.push(xs[i - 1] + extents[i - 1].r + gap + extents[i].l)
  const span = bays.length ? xs[xs.length - 1] : 0
  const length = Math.max(span + END_BEFORE + END_AFTER, MIN_HALL_LENGTH)
  // The hall stays centred on x = 0, as every builder assumes; a hall held
  // open to the minimum keeps its row at the near end and its spare floor
  // past the gas farm.
  return { xs, span, length, x0: -length / 2 + END_BEFORE }
}

/**
 * Stand a list of bays along the aisle, each clear of the last by `gap`, and
 * fit the hall to them. Answers where the row starts and how long it is, so
 * the caller can put the axis tools at the ends of it.
 */
function placeBays(
  bays: Array<Array<{ tool: Tool; facing: number }>>,
  gap: number,
): { x0: number; span: number } {
  const { xs, span, length, x0 } = packOffsets(bays, gap)
  bays.forEach((faces, i) => {
    for (const { tool } of faces) tool.position[0] = x0 + xs[i]
  })
  ;(FAB as { length: number }).length = length
  return { x0, span }
}

/** The bays of the hall as it stands, left to right, each with its faces. */
function currentBays(): Array<Array<{ tool: Tool; facing: number }>> {
  const xs = [...new Set(TOOLS.filter((t) => t.position[2] !== 0).map((t) => t.position[0]))].sort((a, b) => a - b)
  return xs.map((bx) =>
    TOOLS.filter((t) => t.position[2] !== 0 && t.position[0] === bx).map((tool) => ({
      tool,
      facing: aisleDirection(tool),
    })),
  )
}

/** Put the airlock and the gas farm back on the ends of a row. */
function placeAxisTools(x0: number, span: number): void {
  const gowning = TOOLS.find((t) => t.kind === 'gowning')
  if (gowning) gowning.position[0] = x0 - 10
  const gas = TOOLS.find((t) => t.kind === 'gas')
  if (gas) gas.position[0] = x0 + span + 6.6
}

function packRow(): void {
  const bays = currentBays()
  if (bays.length < 2) return
  const { x0, span } = placeBays(bays, BALLROOM_BAY_GAP)
  placeAxisTools(x0, span)
  // And wider, for the second row and its aisle (SECOND_ROW_PIVOT_Z).
  ;(FAB as { bayDepth: number }).bayDepth = BALLROOM_BAY_DEPTH
}

/**
 * Every tool's x as authored, by asset tag, from before the row was
 * packed - for the rooms laid out by hand against those positions (the
 * sub-fab's riser bundles and tool-tied gear), which follow their tools by
 * the difference.
 */
export const AUTHORED_TOOL_X: ReadonlyMap<string, number> = new Map(TOOLS.map((t) => [t.assetTag, t.position[0]]))
/** And which side of the aisle it was authored on, for the floors that re-side it. */
const AUTHORED_TOOL_Z: ReadonlyMap<string, number> = new Map(TOOLS.map((t) => [t.assetTag, t.position[2]]))
const AUTHORED_FAB = { length: FAB.length, bayDepth: FAB.bayDepth }

/**
 * THE TWENTY-FIVE STATIONS, whether or not they are standing on this floor.
 *
 * `TOOLS` is the floor; this is the catalogue. They are the same array on
 * Fab A and Fab B and they differ on a floor somebody built, where `TOOLS`
 * holds the subset that plan chose - in that plan's order, at positions this
 * file computed rather than positions anybody authored.
 *
 * The same OBJECTS in both, deliberately: a tool that goes off the floor and
 * comes back is the same tool, with the same id its panel is registered
 * under and the same asset tag its sub-fab gear is stencilled with.
 */
export const CATALOGUE: readonly Tool[] = [...TOOLS]

/**
 * Whether a station has to be on every floor.
 *
 * Only one does. GOWN-00 is not a process tool - it is the way IN, and the
 * airlock at the end of the aisle is positioned from it (`GOWNING` below).
 * A floor without it is a cleanroom with no door, which is not a teaching
 * simplification but a bug you walk into. Everything else is the author's
 * call, including the bulk gas and the facilities face: a fab that has left
 * them out is wrong in an interesting way, and `flow/coverage.ts` says so
 * rather than this file refusing it.
 */
export const REQUIRED_TOOL_IDS: readonly string[] = ['gowning']

/**
 * How much clear floor a bay needs beside it, in each hall.
 *
 * A ballroom's bays are separated by a divider carrying the bay station and
 * the clear floor a cart needs, and nothing else - three metres. A bay in a
 * bay-and-chase hall is a ROOM: its span of bulkhead has to hold a door into
 * the chase, a window, the bay station, the extinguisher and the eyewash,
 * all of which bays.ts places within the bay's own stretch of wall. Six is
 * what those fittings need without crowding, and it is close to the tightest
 * pitch the authored hall uses.
 */
const BAY_GAP: Record<FloorPlan['hall'], number> = { ballroom: BALLROOM_BAY_GAP, bayChase: 6.0 }

/**
 * Lay the floor out for a plan somebody wrote.
 *
 * TWO TOOLS TO A BAY, in the order the plan gives, which is the order a
 * wafer meets them - the same rule the authored hall follows, where CZ-07
 * and WPR-08 face each other across bay 01 because a wafer goes from one to
 * the other. Sides alternate so the first of each pair is on the -z side,
 * which is where bay 01's first tool stands in Fab A.
 *
 * The row is PACKED rather than placed on the authored grid. It has to be:
 * a plan can put the scanner, whose reticle stocker reaches 4.5 m along the
 * wall, in a bay the authored hall had a crystal puller in. Packing from
 * each bay's own extent is the only placement that cannot overlap, and it
 * is the same function the ballroom uses.
 */
const ON_AXIS = (t: Tool): boolean => t.kind === 'gowning' || t.kind === 'gas'

/** The stations a plan names, once each, in the order it names them. */
function chosenTools(tools: readonly string[]): Tool[] {
  const byId = new Map(CATALOGUE.map((t) => [t.id, t]))
  const seen = new Set<string>()
  const chosen: Tool[] = []
  for (const id of [...REQUIRED_TOOL_IDS, ...tools]) {
    if (seen.has(id)) continue
    const tool = byId.get(id)
    // An id from storage that names no station is dropped, not repaired.
    if (!tool) continue
    seen.add(id)
    chosen.push(tool)
  }
  return chosen
}

/**
 * Which face of which bay each station takes: two to a bay, the first on the
 * -z side, in the plan's own order. The z values here are the authored
 * hall's, so a custom floor's row stands on the same two lines every builder
 * in this directory measures from.
 */
function planBays(row: Tool[]): Array<Array<{ tool: Tool; facing: number }>> {
  const bays: Array<Array<{ tool: Tool; facing: number }>> = []
  row.forEach((tool, i) => {
    const z = i % 2 === 0 ? -3.85 : 3.85
    const face = { tool, facing: -Math.sign(z) }
    if (i % 2 === 0) bays.push([face])
    else bays[bays.length - 1].push(face)
  })
  return bays
}

/**
 * How big a hall a floor plan needs, without building it.
 *
 * The authoring dialog shows this beside the tool list, and it is the same
 * arithmetic the hall is actually laid out with - `layOutPlan` calls the
 * same two functions - so the preview cannot promise a hall the builder
 * would not produce.
 */
export function planFootprint(tools: readonly string[], hall: FloorPlan['hall']): { lengthM: number; bays: number } {
  const chosen = chosenTools(tools)
  const bays = planBays(chosen.filter((t) => !ON_AXIS(t)))
  if (!bays.length) return { lengthM: MIN_HALL_LENGTH, bays: 0 }
  return { lengthM: packOffsets(bays, BAY_GAP[hall]).length, bays: bays.length }
}

function layOutPlan(plan: FloorPlan): void {
  const chosen = chosenTools(plan.tools)
  TOOLS.length = 0
  TOOLS.push(...chosen)

  // The airlock and the gas farm stand on the aisle's own axis, at the ends;
  // everything else takes a face in a bay.
  const axis = chosen.filter(ON_AXIS)
  const row = chosen.filter((t) => !ON_AXIS(t))
  for (const t of axis) t.position[2] = 0
  const bays = planBays(row)
  for (const bay of bays) for (const { tool, facing } of bay) tool.position[2] = -facing * 3.85

  if (bays.length) {
    const { x0, span } = placeBays(bays, BAY_GAP[plan.hall])
    placeAxisTools(x0, span)
  } else {
    // A floor with nothing but the airlock on it. Legal, briefly - the
    // authoring dialog starts every empty plan here - and it still has to
    // be a room somebody can stand in rather than a zero-length hall.
    ;(FAB as { length: number }).length = MIN_HALL_LENGTH
    placeAxisTools(-MIN_HALL_LENGTH / 2 + END_BEFORE, 0)
  }
  if (plan.hall === 'ballroom') (FAB as { bayDepth: number }).bayDepth = BALLROOM_BAY_DEPTH
}

/**
 * Lay the row out for a hall - and lay it out AGAIN for the next one.
 *
 * The hall switch is live (hall.ts): the page is not reloaded, the hall is
 * rebuilt in place, so everything this file decided once at import has to
 * be decidable again. Put the whole catalogue back as authored first - which
 * is what makes a custom floor's deletions recoverable, since the tools were
 * only ever taken off `TOOLS`, never destroyed - then pack it for a
 * ballroom, or lay it out for whatever plan a custom hall names. Every
 * builder reads the result at build time.
 *
 * Registered on the hall change ahead of the rebuild - this module is
 * imported before the composition root registers its own listener, and the
 * listeners run in that order.
 */
function applyHallLayout(layout: HallLayout): void {
  TOOLS.length = 0
  TOOLS.push(...CATALOGUE)
  for (const t of TOOLS) {
    t.position[0] = AUTHORED_TOOL_X.get(t.assetTag) ?? t.position[0]
    t.position[2] = AUTHORED_TOOL_Z.get(t.assetTag) ?? t.position[2]
  }
  ;(FAB as { length: number }).length = AUTHORED_FAB.length
  ;(FAB as { bayDepth: number }).bayDepth = AUTHORED_FAB.bayDepth
  const planId = planIdOf(layout)
  const plan = planId ? getPlan(planId) : null
  if (plan) layOutPlan(plan)
  else if (layout === 'ballroom') packRow()
}
applyHallLayout(HALL_LAYOUT)

/**
 * The gowning airlock sits at the far end of the aisle. Entering through it,
 * rather than spawning on the floor, is deliberate - protocol is the first
 * macroscopic competency in the curriculum, and GOWN-00 is now a tool in its
 * own right rather than only a doorway.
 *
 * Six metres past the gowning station, whichever hall the station stands
 * in.
 */
const gowningX = (): number => TOOLS.find((t) => t.kind === 'gowning')?.position[0] ?? -71
export const GOWNING = {
  position: [gowningX() - 6, 0, 0] as Vector3Tuple,
  width: 6,
  depth: 5,
}

// The live switch: re-lay the row, then move the airlock with it. First in
// the listener order, ahead of the sub-fab's and the builders' refreshes
// and the composition root's rebuild, because this module is imported
// before any of them.
onHallChange((to) => {
  applyHallLayout(to)
  GOWNING.position[0] = gowningX() - 6
})

/**
 * Lithography bays run under yellow light because photoresist is sensitive to
 * the blue end of the spectrum. It is the single most recognisable thing about
 * a fab interior and it is a physics fact, so the scene honours it.
 */
export const LITHO_LIGHT_COLOUR = 0xffcf6a
